import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

// Types
export interface UserInvitation {
  id: string;
  email: string;
  role: 'ADMIN' | 'MANAGER' | 'USER';
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  status: 'pending' | 'accepted' | 'expired';
  invitedBy: {
    firstName: string;
    lastName: string;
  };
  user?: {
    firstName: string;
    lastName: string;
  } | null;
}

export interface UserSession {
  id: string;
  userId: string;
  userAgent: string | null;
  ipAddress: string | null;
  lastActiveAt: string;
  createdAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface CreateInvitationDto {
  email: string;
  role?: 'ADMIN' | 'MANAGER' | 'USER';
}

export interface AcceptInvitationDto {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
}

export interface QueryInvitationDto {
  page?: number;
  limit?: number;
  status?: 'pending' | 'accepted' | 'expired';
}

export interface QuerySessionsDto {
  page?: number;
  limit?: number;
  userId?: string;
}

// Query keys
export const adminKeys = {
  invitations: ['admin', 'invitations'] as const,
  invitationList: (filters?: QueryInvitationDto) => [...adminKeys.invitations, 'list', filters] as const,
  invitationDetail: (id: string) => [...adminKeys.invitations, 'detail', id] as const,
  invitationByToken: (token: string) => [...adminKeys.invitations, 'token', token] as const,
  sessions: ['admin', 'sessions'] as const,
  sessionList: (filters?: QuerySessionsDto) => [...adminKeys.sessions, 'list', filters] as const,
};

// ============================================
// INVITATIONS
// ============================================

const fetchInvitationList = async (query: QueryInvitationDto = {}) => {
  const params = new URLSearchParams();
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.status) params.append('status', query.status);

  const response = await apiClient.get(`/invitations?${params.toString()}`);
  return response.data;
};

const fetchInvitationByToken = async (token: string) => {
  const response = await apiClient.get(`/invitations/token/${token}`);
  return response.data;
};

const createInvitation = async (data: CreateInvitationDto) => {
  const response = await apiClient.post('/invitations', data);
  return response.data;
};

const acceptInvitation = async (data: AcceptInvitationDto) => {
  const response = await apiClient.post('/invitations/accept', data);
  return response.data;
};

const resendInvitation = async (id: string) => {
  const response = await apiClient.post(`/invitations/${id}/resend`);
  return response.data;
};

const revokeInvitation = async (id: string) => {
  await apiClient.delete(`/invitations/${id}`);
};

export function useInvitationList(query: QueryInvitationDto = {}) {
  return useQuery({
    queryKey: adminKeys.invitationList(query),
    queryFn: () => fetchInvitationList(query),
  });
}

export function useInvitationByToken(token: string) {
  return useQuery({
    queryKey: adminKeys.invitationByToken(token),
    queryFn: () => fetchInvitationByToken(token),
    enabled: !!token,
    retry: false,
  });
}

export function useCreateInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.invitations });
    },
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: acceptInvitation,
  });
}

export function useResendInvitation() {
  return useMutation({
    mutationFn: resendInvitation,
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.invitations });
    },
  });
}

// ============================================
// SESSIONS
// ============================================

const fetchSessionList = async (query: QuerySessionsDto = {}) => {
  const params = new URLSearchParams();
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.userId) params.append('userId', query.userId);

  const response = await apiClient.get(`/admin/sessions?${params.toString()}`);
  return response.data;
};

const forceLogoutSession = async (sessionId: string) => {
  await apiClient.delete(`/admin/sessions/${sessionId}`);
};

const forceLogoutUser = async (userId: string) => {
  const response = await apiClient.delete(`/admin/sessions/user/${userId}`);
  return response.data;
};

export function useSessionList(query: QuerySessionsDto = {}) {
  return useQuery({
    queryKey: adminKeys.sessionList(query),
    queryFn: () => fetchSessionList(query),
  });
}

export function useForceLogoutSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: forceLogoutSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.sessions });
    },
  });
}

export function useForceLogoutUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: forceLogoutUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.sessions });
    },
  });
}

// ============================================
// EXPORT
// ============================================

const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export function useExportBewerber() {
  return useMutation({
    mutationFn: async (query: { status?: string; search?: string } = {}) => {
      const params = new URLSearchParams();
      if (query.status) params.append('status', query.status);
      if (query.search) params.append('search', query.search);

      const response = await apiClient.get(`/admin/export/bewerber?${params.toString()}`, {
        responseType: 'blob',
      });
      const filename = `bewerber-export-${new Date().toISOString().split('T')[0]}.csv`;
      downloadFile(response.data, filename);
    },
  });
}

export function useExportBedarfe() {
  return useMutation({
    mutationFn: async (query: { status?: string; search?: string } = {}) => {
      const params = new URLSearchParams();
      if (query.status) params.append('status', query.status);
      if (query.search) params.append('search', query.search);

      const response = await apiClient.get(`/admin/export/bedarfe?${params.toString()}`, {
        responseType: 'blob',
      });
      const filename = `bedarfe-export-${new Date().toISOString().split('T')[0]}.csv`;
      downloadFile(response.data, filename);
    },
  });
}

export function useExportAuditLogs() {
  return useMutation({
    mutationFn: async (query: { action?: string; entityType?: string; startDate?: string; endDate?: string } = {}) => {
      const params = new URLSearchParams();
      if (query.action) params.append('action', query.action);
      if (query.entityType) params.append('entityType', query.entityType);
      if (query.startDate) params.append('startDate', query.startDate);
      if (query.endDate) params.append('endDate', query.endDate);

      const response = await apiClient.get(`/admin/export/audit-logs?${params.toString()}`, {
        responseType: 'blob',
      });
      const filename = `audit-logs-export-${new Date().toISOString().split('T')[0]}.csv`;
      downloadFile(response.data, filename);
    },
  });
}
